import i18n from '@/i18n';
import config from '@/config';
import axios from 'axios';
import { AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useFhirStore } from '@/stores/fhirStore';
import type {
  Bundle,
  Group,
  BundleEntry,
  Parameters,
  ParametersParameter,
  ResearchStudy,
  Resource,
  Reference,
  FhirResource,
  Coding,
  Basic,
  Location,
} from 'fhir/r4';
import ChangeRequestTask from '@/tsClasses/fhir/changeRequestTask';
import { TaskStatus } from '@/tsEnums/TaskStatus';
import SearchQueryStore from '@/tsClasses/fhir/SearchQueryStore';

const { t } = i18n.global;

const getAuthConfig = () => {
  const authStore = useAuthStore();

  return {
    headers: {
      Authorization: `Bearer ${authStore.keycloak?.token}`,
    },
  };
};

const getNoCacheHeaderConfig = () => {
  return {
    headers: {
      'Cache-Control': 'no-cache, must-revalidate, max-age=0', //Prevent browser caching on current browsers (will not work for IE6). Removed 'no-store' as that seems to sometimes interfere with hapi pagination, causing Bundle.link to have no 'next'.
      ...getAuthConfig().headers,
    },
  };
};

const fhirBaseUrl = config.FHIR_URL;

if (!fhirBaseUrl || fhirBaseUrl === '') {
  throw 'No FHIR base url for fhir server specified.';
}

const terminologyBaseUrl = config.TERMINOLOGY_URL;

/**
 * Performs a POST to the '/Location' endpoint on the fhir server.
 * @param {Object} newLocation Fhir Location resource
 * @returns Fhir server response
 */
const postLocationResource = function (newLocation: Location) {
  return postFhirResource('/Location', newLocation);
};

/**
 * Performs a POST to Fhir server. @param urlSuffix is added to fhir base url, e.g. '/Location'.
 *
 * @param {String} urlSuffix Is appended to fhir base url, e.g. '/Location'
 * @param {Object} resource Resource to be posted
 * @returns Respone of Fhir server
 */
const postFhirResource = function (
  urlSuffix: string,
  resource: Resource
): Promise<AxiosResponse<object, object>> {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  return axios.post(fhirBaseUrl + urlSuffix, resource, authAndNoCacheConfig);
};

/**
 * Performs a POST to the fhir root url. Use for bundles.
 *
 * @param {Object} bundle Fhir Bundle
 * @returns Fhir server response
 */
const postFhirBundle = function (
  bundle: Bundle
): Promise<AxiosResponse<object, object>> {
  return postFhirResource('/', bundle);
};

/**
 * Performs a meta-delete operation an a Resource.
 * 
 * @param {String} urlSuffix designates the targeted resource, e.g. "/ResearchStudy/<id>"
 * @param {String} parameterJson Payload, e.g. {
        resourceType: "Parameters",
        parameter: {
          name: "meta",
          valueMeta: {
            tag: {
              system: system,
              code: code,
            },
          },
        },
      }
 * @returns Response of fhir server
 */
const metaDelete = function (
  urlSuffix: string,
  parameterJson: Parameters
): Promise<AxiosResponse<object, object>> {
  return axios.post(
    fhirBaseUrl + urlSuffix + '/$meta-delete',
    parameterJson,
    getAuthConfig()
  );
};

/**
 * Performs a PUT operation to the fhir server.
 *
 * @param {String} urlSuffix Specifies targeted resource, e.g. '/ResearchStudy/<id>'
 * @param {Resource} resource Resource payload
 * @returns Fhir server response
 */
const putFhirResource = function (
  urlSuffix: string,
  resource: Resource
): Promise<AxiosResponse<object, object>> {
  return axios.put(fhirBaseUrl + urlSuffix, resource, getAuthConfig());
};

/**
 * Perform delete on Fhir server for given Group resource. Keep in mind, that resources that are references from within other resources generally can't be deleted.
 * @param {Object} groupObj Group resource to be deleted
 * @returns Fhir server response
 */
const deleteGroup = function (
  groupObj: Group
): Promise<AxiosResponse<object, object>> {
  return deleteFhirResource('/Group/' + groupObj.id);
};

/**
 * Performs delete operation
 * @param {String} urlSuffix Specifies target, e.g. "/Group/<id>"
 * @returns Fhir server response
 */
const deleteFhirResource = function (
  urlSuffix: string
): Promise<AxiosResponse<object, object>> {
  return axios.delete(fhirBaseUrl + urlSuffix, getAuthConfig());
};

/**
 * Invokes $add-concept-to-medication operation on fhir server.
 * @param {Parameters} substanceMessageObject Contains info about new substance. FHIR Parameters object. Structure: {
        resourceType: "Parameters",
        parameter: [
          {
            name: "code",
            valueString: <substanceCode>,
          },
          {
            name: "display",
            valueString: <substanceName>,
          },
          {
            name: "substanceOrProtocol",
            valueString: "substance",
          },
        ],
      }
 * @returns Fhir server response
 */
const addConceptToMedication = function (
  substanceMessageObject: Parameters
): Promise<AxiosResponse<object, object>> {
  return axios.post(
    fhirBaseUrl + '/ValueSet/$add-concept-to-medication',
    substanceMessageObject,
    {
      ...getAuthConfig(),
      validateStatus: function (status) {
        return status < 500; // Resolve only if the status code is less than 500. Allows for processing of 4xx codes, which are returned e.g. when a substance is added that is already in the CodeSystem.
      },
    }
  );
};

/**
 * Performs a GET operation on the Fhir server.
 * @param {String} urlSuffix Specify target, e.g. "/ResearchStudy/<id>"
 * @param {Boolean} useTerminologyServer If true, terminology base url is used. Otherwise, standard fhir server base url is used.
 * @returns Server response
 */
const getFhirResource = function (
  urlSuffix: string,
  useTerminologyServer: boolean
): Promise<AxiosResponse<object, object>> {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  const baseUrl =
    useTerminologyServer === true ? terminologyBaseUrl : fhirBaseUrl;

  return axios.get(baseUrl + urlSuffix, authAndNoCacheConfig);
};

/**
 * Performs a GET operation on the Fhir server specifically for a Group resource.
 * @param {String} groupId ID of targeted Group resource
 * @returns Server response
 */
const getGroupById = function (
  groupId: string
): Promise<AxiosResponse<object, object>> {
  return getFhirResource('/Group/' + groupId + '?_format=json', false);
};

/**
 * Searches CodeSystem based on given url.
 * @param {String} url Url of targeted CodeSystem resource
 * @param {Boolean} useTerminologyServer If true, terminology base url is used. Otherwise, standard fhir server base url is used.
 * @returns Server response
 */
const searchCodeSystemByUrl = function (
  url: string,
  useTerminologyServer: boolean
): Promise<AxiosResponse<object, object>> {
  return getFhirResource('/CodeSystem/?url=' + url, useTerminologyServer);
};

/**
 * Searches ValueSet based on given url.
 * @param {String} url Url of targeted ValueSet resource
 * @param {Boolean} useTerminologyServer If true, terminology base url is used. Otherwise, standard fhir server base url is used.
 * @returns Server response
 */
const searchValueSetByUrl = function (
  url: string,
  useTerminologyServer: boolean
): Promise<AxiosResponse<object, object>> {
  return getFhirResource('/ValueSet/?url=' + url, useTerminologyServer);
};

/**
 * Searches ValueSet based on given url and expands the result.
 * @param {String} url Url of targeted ValueSet resource
 * @param {Boolean} useTerminologyServer If true, terminology base url is used. Otherwise, standard fhir server base url is used.
 * @returns Server response
 */
const searchAndExpandValueSetByUrl = function (
  url: string,
  useTerminologyServer: boolean
): Promise<AxiosResponse<object, object>> {
  return getFhirResource('/ValueSet/$expand?url=' + url, useTerminologyServer);
};

/**
 * Searches ConceptMap based on given url and expands the result.
 * @param {String} url Url of targeted resource
 * @param {Boolean} useTerminologyServer If true, terminology base url is used. Otherwise, standard fhir server base url is used.
 * @returns Server response
 */
const searchConceptMapByUrl = function (
  url: string,
  useTerminologyServer: boolean
): Promise<AxiosResponse<object, object>> {
  return getFhirResource('/ConceptMap/?url=' + url, useTerminologyServer);
};

/**
 * Performs a GET operation on the Fhir server for Locations with @param locationName as the 'name' parameter. Can return more than one Location.
 * @param {String} locationName name of Location to be searched
 * @param {boolean} contains if true a 'contains' search is conducted, otherswise a 'startsWith' search
 * @returns Server response
 */
const searchLocationsByName = function (
  locationName: string,
  contains: boolean
): Promise<AxiosResponse<object, object>> {
  if (contains) {
    return getFhirResource('/Location/?name:contains=' + locationName, false);
  } else {
    return getFhirResource('/Location/?name=' + locationName, false);
  }
};

class FhirServerResponseNotBundleError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}

class NoResultForIdentifierSearchError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}

class AmbivalentResultsForIdentifierSearchError extends Error {
  constructor(msg: string) {
    super(msg);
  }
}

/**
 * Performs a search on the Fhir server for a ResearchStudy by identifier system and code.
 * @param {String} identifierSystem System of identifier. May be url encoded.
 * @param {String} identifierValue Identifier of targeted resource. May be url encoded.
 * @returns Extracted ResearchStudy, if one is found. Error if none is found, Error if more than one is found.
 */
const getResearchStudyByIdentifier = async function (
  identifierSystem: string,
  identifierValue: string,
  summaryMode: boolean = false
): Promise<ResearchStudy> {
  const result = await getFhirResource(
    encodeURI(
      `/ResearchStudy?identifier=${decodeURI(identifierSystem)}|${decodeURI(identifierValue)}&_format=json${summaryMode ? '&_summary=true' : ''}`
    ),
    false
  );

  if ('resourceType' in result.data && result.data.resourceType === 'Bundle') {
    const resultingBundle: Bundle = result.data as Bundle;
    if (!resultingBundle.entry || resultingBundle.entry.length < 1) {
      throw new NoResultForIdentifierSearchError(
        'Identifier search returned no results.'
      );
    }
    if (resultingBundle.entry.length > 1) {
      throw new AmbivalentResultsForIdentifierSearchError(
        'Received more than one result for identifier search.'
      );
    }

    return resultingBundle.entry[0].resource as ResearchStudy;
  } else {
    console.error(
      'Unexpected response from FHIR server: ' + JSON.stringify(result.data)
    );
    throw new FhirServerResponseNotBundleError(
      'Response from FHIR server was expected to be a FHIR Bundle, but was not.'
    );
  }
};

/**
 * Performs a GET operation on the Fhir server specifically for a ResearchStudy resource.
 * @param {String} resourceId ID of targeted resource
 * @returns Server response
 */
const getResearchStudyById = function (
  resourceId
): Promise<AxiosResponse<object, object>> {
  return getFhirResource(
    '/ResearchStudy/' + resourceId + '?_format=json',
    false
  );
};

/**
 * Performs a $translate operation on a given ConceptMap for a given code.
 * @param {String} code Code to be translated
 * @param {String} conceptMapUrl url specifying the target CodeSystem
 * @returns Server response
 */
const translateOperationOnConceptMap = function (
  code: string,
  conceptMapUrl: string
): Promise<AxiosResponse<object, object>> {
  return axios.get(
    fhirBaseUrl +
      '/ConceptMap/$translate?code=' +
      code +
      '&url=' +
      conceptMapUrl,
    getAuthConfig()
  );
};

/**
 * Expands given ValueSet and applies filter to it with given filter parameter.
 * @param {String} valuesetUrl Url of targeted ValueSet
 * @param {String} displayValueForFiltering input for filter operation
 * @returns Server response
 */
const filterExpandedValueSet = function (
  valuesetUrl: string,
  displayValueForFiltering: string
): Promise<AxiosResponse<object, object>> {
  return axios.get(
    fhirBaseUrl +
      '/ValueSet/$expand/?url=' +
      valuesetUrl +
      '&filter=' +
      displayValueForFiltering +
      '&_total=accurate',
    getAuthConfig()
  );
};

/**
 * Sends a query for a $translate operation to the FHIR server.
 * @param event is the input event, containing a code to be used as parameter for $translate
 * @param category "Tumorregion", "Tumorentität" or "Tumorcategory"; determines which ConceptMap is to be used.
 *
 * @return Array of objects with system, code and display. Each one represents one diagnosis.
 */
async function getDiagnosesForCategory(
  event: { code: string; display?: string } | Coding,
  category: string //TODO Maybe use enum?
): Promise<Array<{ system?: string; code: string; display?: string }>> {
  const fhirStore = useFhirStore();
  let conceptMapUrl: string | undefined = undefined;
  //Get ConceptMap url, depending on category code
  switch (category) {
    case t('trialsList.tumorregion').toString():
      conceptMapUrl = fhirStore.urls.tumorregionConceptMapUrl;
      break;

    case t('trialsList.tumorentity').toString():
      conceptMapUrl = fhirStore.urls.tumorentityConceptMapUrl;
      break;

    case t('trialsList.tumorcategory').toString():
      //In this case the url depends on the given code -> Additional Switch because several more category codes are expected to be added in the future.
      switch (event.code) {
        case 'solidTumor':
          conceptMapUrl = fhirStore.urls.solidTumorsConceptMapUrl;
          break;

        //Add more cases as categories are added.

        default:
          break;
      }
      break;

    default:
      break;
  }

  if (conceptMapUrl === undefined) {
    //Don't try to make a request with an undefined url.
    throw new TypeError(
      `Expected valid conceptMapUrl, but got undefined. Category was: ${category}, event was ${JSON.stringify(
        event
      )}.`
    );
  }

  const response: AxiosResponse = await translateOperationOnConceptMap(
    event.code ?? '',
    conceptMapUrl
  );

  //Get CodeableConcept with code, system and display
  const result = response.data.parameter
    .filter((param: ParametersParameter) => param.name === 'match')
    .filter((p: ParametersParameter) => p.part != undefined)
    .filter((p: ParametersParameter) =>
      p.part?.filter((part) => part.name === 'concept')
    )
    .map((p: ParametersParameter) =>
      p.part
        ?.filter((p: ParametersParameter) => p.name === 'concept')
        .map((p) => p.valueCoding)
    )
    .map(
      (arr: Array<{ system?: string; code: string; display?: string }>) =>
        arr[0]
    );

  return result;
}

/**
 * Takes a Group id as parameter and attempts to find the ResearchStudy that this Group belongs to. Assumes that a Group is only used by one ResearchStudy.
 */
const getResearchStudyByGroupId = async function (
  groupId: string
): Promise<ResearchStudy | undefined> {
  //Make _revinclude request
  const response: AxiosResponse = await axios.get(
    `${fhirBaseUrl}/Group/?_id=${groupId}&_revinclude=ResearchStudy:enrollment`,
    getAuthConfig()
  );

  const responseEntry = response.data.entry;
  //Extract ResearchStudy, if possible
  if (responseEntry) {
    if (response.data.entry.length > 1) {
      console.log({
        msg: 'Several matches found while trying to find ResearchStudy for a given Group resource.',
        groupId,
        response,
      });
    } else {
      const researchStudyEntry = responseEntry.find(
        (entry: BundleEntry) =>
          entry.resource && entry.resource.resourceType === 'ResearchStudy'
      );

      return researchStudyEntry?.resource;
    }
  } else {
    console.log({
      msg: 'No results found while trying to find ResearchStudy for a given Group resource.',
      groupId,
      response,
    });
  }
};

/**
 * Takes an array of FHIR references, builds and sends a fhir query for Task resources with the ChangeRequestProfile that have one of the given references as focus and returns the Task resources.
 * @param references Reference objects / Strings of structure <ResourceType>/<ResourceId>
 * @param statusSearchParamValue status values the Tasks should have. If empty, no status parameter is set in the query. Can contain several status values from ValueSet 'http://hl7.org/fhir/task-status' separated by comma, e.g. 'rejected,requested'.
 * @returns Task resources that have the ChangeRequest profile and that have one of the given references in Task.focus.
 */
const getChangeRequestsForResourceIdentifiers = async function (
  references: Reference[],
  statusSearchParamValue: string | undefined
) {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  const fhirStore = useFhirStore();
  const changeRequestProfile = fhirStore.urls.changeRequestTaskUrl;

  const focusReferencesList: string = references.join(','); //Convert the array to a string with all references separated by a comma

  const statusSearchParam = statusSearchParamValue
    ? `&status=${statusSearchParamValue}`
    : '';

  const request = axios.get(
    `${fhirBaseUrl}/Task/?_profile=${changeRequestProfile}${statusSearchParam}&focus=${focusReferencesList}&_total=accurate`,
    authAndNoCacheConfig
  );

  const responseBundle = (await request).data;
  //Possibly several pages -> navigate through pages
  return (await getResourcesFromAllBundlePages(
    responseBundle
  )) as Array<ChangeRequestTask>;
};

const getResourcesFromBundleEntries = function (bundleEntries: BundleEntry[]) {
  if (!bundleEntries) return undefined;

  const currentBundleResources: FhirResource[] | undefined = bundleEntries
    .filter((entry) => entry.resource !== undefined)
    .map((entry) => entry.resource as FhirResource);

  return currentBundleResources;
};

/**
 * Follows all 'next' links in a given Bundle and retrieves the contained resources. Returns all obtained resources.
 * @param initialBundle Fhir Bundle
 * @returns resources from all Bundles
 */
const getResourcesFromAllBundlePages = async function (initialBundle: Bundle) {
  const allExtractedResources: FhirResource[] = new Array<FhirResource>();
  //Extract resources from initial Bundle
  if (initialBundle.entry) {
    const initialBundleResources: FhirResource[] =
      getResourcesFromBundleEntries(initialBundle.entry) as FhirResource[];
    initialBundleResources.forEach((resource) =>
      allExtractedResources.push(resource)
    );
  }

  let nextLink = getBundleNextLink(initialBundle);
  //Follow next link, get Bundle, extract resources and next link and so on
  while (nextLink) {
    //Get next Bundle following nextLink
    const nextBundle = (await axios.get(nextLink.url, getAuthConfig())).data;
    if (nextBundle.entry) {
      const currentBundleResources = getResourcesFromBundleEntries(
        nextBundle.entry
      );
      currentBundleResources?.forEach((resource) =>
        allExtractedResources.push(resource)
      );
    }

    //Get link for next iteration
    nextLink = getBundleNextLink(nextBundle);
  }

  return allExtractedResources;
};

const getBundleNextLink = function (bundle: Bundle) {
  return bundle.link?.find((entry) => entry.relation === 'next');
};

const getChangeRequestTaskSummaryWithStatusForRequester = function (
  taskStatus: TaskStatus,
  requester: string //user id
) {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  return axios.get(
    `${fhirBaseUrl}/Task/?_summary=true&_total=accurate&status=${taskStatus}&_profile=${
      useFhirStore().urls.changeRequestTaskUrl
    }&requester=${requester}`,
    authAndNoCacheConfig
  );
};

const getChangeRequestTaskSummaryWithStatus = function (
  taskStatus: TaskStatus
) {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  return axios.get(
    `${fhirBaseUrl}/Task/?_summary=true&_total=accurate&status=${taskStatus}&_profile=${
      useFhirStore().urls.changeRequestTaskUrl
    }`,
    authAndNoCacheConfig
  );
};

/**
 * Builds a transaction Bundle for GET requests.
 * @param references The given strings are expected to follow the pattern <ResourceType>/<ResourceId>, optionally with a leading slash.
 * @returns result of POST as Promise
 */
const getViaTransactionBundle = function (
  references: Iterable<string | Reference>
): Promise<AxiosResponse<Bundle>> {
  const authAndNoCacheConfig = {
    //Set headers for auth and cache control to prevent caching
    ...{
      headers: {
        ...getNoCacheHeaderConfig().headers,
        ...getAuthConfig().headers,
      },
    },
  };

  // Build Transaction Bundle to get all referenced resources
  const bundleEntries = Array.from(references)
    .map((reference) => {
      if (typeof reference === 'string') return reference;
      else return reference.reference;
    })
    .filter((reference) => reference !== undefined)
    .map((reference) => {
      //Add leading slash if necessary
      if ((reference as string).charAt(0) === '/') return reference;
      else return '/' + reference;
    })
    .map((reference) => ({
      request: {
        method: 'GET',
        url: reference,
      },
    }));

  const transactionBundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: bundleEntries,
  };

  return axios.post(fhirBaseUrl, transactionBundle, authAndNoCacheConfig);
};

/**
 * Tries to get the SearchQueryStore resource for the user whose id is passed as practitionerId. If none is found, a new one is created.
 * @param userFhirRepresentationId User ID for which the search query store is searched
 * @returns a Basic resource with the SearchQueryStore profile that stores the search profile for the user with the given id.
 */
const getSearchQueryStoreForUser = async function (
  userFhirRepresentationId
): Promise<Basic | undefined> {
  const response = await getFhirResource(
    '/Basic?subject=' + userFhirRepresentationId,
    false
  );

  //Extract SearchQueryStore from response
  if (response.status === 200 && response.data) {
    if ('entry' in response.data) {
      const returnedQueryStores = (response.data as Bundle).entry?.filter(
        (e) => e.resource?.resourceType === 'Basic'
      );

      if (returnedQueryStores && returnedQueryStores.length === 1) {
        return returnedQueryStores[0].resource as Basic;
      }

      if (returnedQueryStores && returnedQueryStores.length > 1) {
        console.warn({
          msg: `Received more than one query store for current user with id ${userFhirRepresentationId}. Using first one.`,
          returnedQueryStores,
        });

        return returnedQueryStores[0].resource as Basic;
      }
    } else {
      //If none is found -> Create one, post it, return it.
      //TODO Could be nice to encapsulate this logic in a backend service endpoint instead of doing it in the frontend.
      const newSearchQueryStore = new SearchQueryStore(
        userFhirRepresentationId
      );
      const postResponse = await postFhirResource(
        '/Basic',
        newSearchQueryStore
      );

      if (postResponse.status >= 200 && postResponse.status < 300) {
        if (
          postResponse.data &&
          'resourceType' in postResponse.data &&
          postResponse.data.resourceType === 'Basic'
        ) {
          return postResponse.data as Basic;
        }
      }
      // Could be useful to add backend endpoint to register such failures (?) -> https://git-ee.molit.eu/molit-projects/cats/cats-backend/-/issues/91
      throw new Error(
        `POST for new user SearchQueryStore failed. FHIR server responded with code ${postResponse.status} ${postResponse.statusText}. Response was ${postResponse}`
      );
    }
  } else {
    // Could be useful to add backend endpoint to register such failures (?) -> https://git-ee.molit.eu/molit-projects/cats/cats-backend/-/issues/91
    throw new Error(
      `Query for user SearchQueryStore failed. FHIR server responded with code ${response.status} ${response.statusText}. Response was ${response}`
    );
  }
};

export default {
  getAuthConfig,
  getResourcesFromAllBundlePages,
  getResourcesFromBundleEntries,
  getBundleNextLink,
  postLocationResource,
  postFhirResource,
  metaDelete,
  putFhirResource,
  postFhirBundle,
  deleteGroup,
  deleteFhirResource,
  addConceptToMedication,
  getFhirResource,
  getGroupById,
  getResearchStudyById,
  translateOperationOnConceptMap,
  filterExpandedValueSet,
  searchLocationsByName,
  searchCodeSystemByUrl,
  searchValueSetByUrl,
  searchAndExpandValueSetByUrl,
  searchConceptMapByUrl,
  getDiagnosesForCategory,
  getResearchStudyByGroupId,
  getChangeRequestsForResourceIdentifiers,
  getChangeRequestTaskSummaryWithStatus,
  getChangeRequestTaskSummaryWithStatusForRequester,
  getViaTransactionBundle,
  getSearchQueryStoreForUser,
  getResearchStudyByIdentifier,
};
